/*
 * DD motion runtime.
 *
 * Progressive enhancement only — every hiding attribute/class is added by
 * this script, so no-JS and reduced-motion users see the static page.
 *
 *   1. Hero entrance: tags the homepage hero's content children with
 *      [data-dd-hero-intro] + --dd-stagger-i before first paint, so the
 *      one-time entrance (dd-motion.css) plays without a flash.
 *   2. Magnetic CTAs: primary buttons drift a few px toward the cursor
 *      (desktop hover only, transform-only, rAF-lerped, eases back on leave).
 */

(() => {
  const motionOK = matchMedia('(prefers-reduced-motion: no-preference)').matches;
  if (!motionOK) return;

  /* ─── 1. Hero entrance ─────────────────────────────────────────────────── */

  const main = document.querySelector('main[data-template="index"]');
  const firstSection = main?.querySelector(':scope > .shopify-section');
  const heroContent = firstSection?.querySelector('.hero__content-wrapper');

  if (heroContent) {
    Array.from(heroContent.children).forEach((child, index) => {
      child.style.setProperty('--dd-stagger-i', index);
    });
    heroContent.setAttribute('data-dd-hero-intro', '');
  }

  /* ─── 2. Magnetic CTAs ─────────────────────────────────────────────────── */

  const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (!finePointer) return;

  const MAGNETIC_SELECTOR = [
    'a.button:not(.button-unstyled)',
    'a.button-secondary',
    '.button-primary',
    '[data-dd-magnetic]',
  ].join(', ');
  const MAX_OFFSET = 6; // px — small radius, keeps the effect tasteful
  const LERP = 0.18;

  document.querySelectorAll(MAGNETIC_SELECTOR).forEach((el) => {
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let rafId = 0;

    const tick = () => {
      currentX += (targetX - currentX) * LERP;
      currentY += (targetY - currentY) * LERP;

      if (Math.abs(currentX) < 0.1 && Math.abs(currentY) < 0.1 && targetX === 0 && targetY === 0) {
        el.style.transform = '';
        rafId = 0;
        return;
      }

      el.style.transform = `translate(${currentX.toFixed(2)}px, ${currentY.toFixed(2)}px)`;
      rafId = requestAnimationFrame(tick);
    };

    const start = () => {
      if (!rafId) rafId = requestAnimationFrame(tick);
    };

    // DD-PERF: cached on enter rather than read per pointermove. tick() writes el.style.transform
    // from the rAF loop, and transform feeds the geometry getBoundingClientRect() reads, so
    // measuring inside the move handler forced a synchronous layout flush on every event.
    /** @type {DOMRect | null} */
    let rect = null;

    el.addEventListener('pointerenter', () => {
      el.classList.add('dd-magnetic');
      rect = el.getBoundingClientRect();
    });

    /** @param {PointerEvent} event */
    const handlePointerMove = (event) => {
      const bounds = rect ?? el.getBoundingClientRect();
      rect = bounds;
      targetX = Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, ((event.clientX - bounds.left) / bounds.width - 0.5) * MAX_OFFSET * 2));
      targetY = Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, ((event.clientY - bounds.top) / bounds.height - 0.5) * MAX_OFFSET * 2));
      start();
    };

    el.addEventListener('pointermove', /** @type {EventListener} */ (handlePointerMove), { passive: true });

    el.addEventListener('pointerleave', () => {
      // Drop `dd-magnetic` on the way out: dd-motion.css sets `will-change: transform` on it, so
      // leaving it applied kept a promoted compositing layer on every button hovered this session.
      el.classList.remove('dd-magnetic');
      rect = null;
      targetX = 0;
      targetY = 0;
      start();
    });
  });
})();
